import { useQuery } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';
import { BarStack } from '@visx/shape';
import { SeriesPoint } from '@visx/shape/lib/types';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { timeFormat, timeParse } from 'd3-time-format';
import { defaultStyles, Tooltip, useTooltip } from '@visx/tooltip';
import { Box, Flex, Text } from 'rebass';
import colors from '../../theme/colors';
import theme from '../../theme';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { useStoreFilters } from '../../hooks/useStoredFilters';
import {
  AssemblyFilters,
  defaultFilters,
} from '../../pages/assemblies/FilterBarAssembly';
import { endOfMonth } from 'date-fns';
import Legend from '../Chart/Legend';
import { assignColumnAccessor } from '../../hooks/useTablePages';
import papaparse from 'papaparse';
import { downloadFile } from '../../helpers/download';
import Button from '../Button/Button';
import { gql } from '@/modes/waterSupplier/gql';

const TEST_STATUS_QUERY = gql(`
  query TestStatusByMonth {
    testStatusByMonth: backflowAssemblies(
      aggregate: [{ col: "id", type: DISTINCT, as: "backflowAssemblies" }]
      where: { status: "ACTIVE", testAnniversaryMonth: { not: null } }
      groupBy: ["testAnniversaryMonth", "testStatus"]
      orderBy: [{ col: "testAnniversaryMonth" }]
      limit: null
    ) {
      testAnniversaryMonth
      testStatus
      agg
    }
  }
`);

type TestStatus = 'APPROVED' | 'COMPLETED' | 'SCHEDULED' | 'NOT_SCHEDULED';

type TestStatusData = {
  testStatusByMonth: {
    testAnniversaryMonth: number;
    testStatus: TestStatus;
    agg: {
      backflowAssemblies: number;
    };
  }[];
};

type TooltipData = {
  bar: SeriesPoint<Datum>;
  key: TestStatus;
  index: number;
  height: number;
  width: number;
  x: number;
  y: number;
  color: string;
};

type BarStackProps = {
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  events?: boolean;
};

type Datum = { month: number } & Record<TestStatus, number>;

const defaultMargin = { top: 40, right: 0, bottom: 0, left: 0 };

const tooltipStyles: React.CSSProperties = {
  ...defaultStyles,
  minWidth: 180,
  backgroundColor: 'white',
  color: colors.text,
  position: 'absolute',
  borderRadius: theme.radii.default,
  boxShadow: theme.shadows.menu,
  padding: theme.space[3],
  opacity: 0.95,
  transition: 'all .3s ease',
};

const parseDate = timeParse('%m');
const format = timeFormat('%b');
const longFormat = timeFormat('%B');
const formatDate = (month: number) => format(parseDate(month + '') as Date);
const formatDateLong = (month: number) =>
  longFormat(parseDate(month + '') as Date);

// accessors
const getMonth = (d: Datum) => d.month;

let tooltipTimeout: number;

const Visx = ({
  width,
  height,
  events = true,
  margin = defaultMargin,
}: BarStackProps) => {
  const { data: testStatusData } = useQuery<TestStatusData>(TEST_STATUS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const testStatusByMonth = testStatusData?.testStatusByMonth || [];

  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>();

  const history = useHistory();
  const mode = useMode();
  const storeFilters = useStoreFilters<AssemblyFilters>('backflowAssemblies');

  // bounds
  const xMax = width;
  const yMax = height - margin.top - 25;

  const statuses = Array.from(
    new Set(testStatusByMonth.map((row) => row.testStatus)).values()
  ) as TestStatus[];

  const [disabledKeys, setDisabledKeys] = useState(
    {} as Record<TestStatus, boolean | undefined>
  );

  const toggleKey = useCallback((key: TestStatus) => {
    setDisabledKeys((oldValue) => {
      return {
        ...oldValue,
        [key]: !oldValue[key],
      };
    });
  }, []);

  const keys = statuses.filter((status) => !disabledKeys[status]);

  const months = Array.from({ length: 12 }, (v, k) => k + 1);

  const data = useMemo(
    () =>
      months.reduce((allTotals, currentMonth) => {
        const monthEntry = testStatusByMonth
          .filter((row) => row.testAnniversaryMonth === currentMonth)
          .reduce(
            (monthEntry, current) => {
              monthEntry[current.testStatus] = current.agg.backflowAssemblies;
              return monthEntry;
            },
            {
              month: currentMonth,
              APPROVED: 0,
              COMPLETED: 0,
              NOT_SCHEDULED: 0,
              SCHEDULED: 0,
            } as Datum
          );
        allTotals.push(monthEntry);
        return allTotals;
      }, [] as Datum[]),
    [testStatusByMonth]
  );

  const monthTotals = useMemo(
    () =>
      data.reduce((allTotals, currentMonth) => {
        const monthTotal = keys.reduce((total, sender) => {
          return total + currentMonth[sender];
        }, 0);
        allTotals.push(monthTotal);
        return allTotals;
      }, [] as number[]),
    [data, disabledKeys]
  );

  if (width < 10 || !testStatusByMonth) return <></>;

  // scales
  const dateScale = scaleBand<number>({
    domain: months,
    paddingInner: 0.1,
  });

  const yScale = scaleLinear<number>({
    domain: [0, Math.max(...monthTotals)],
    range: [yMax, 0],
    nice: true,
  });

  const colorMap: Record<TestStatus, string> = {
    APPROVED: colors.statusSuccess,
    COMPLETED: colors.statusPending,
    NOT_SCHEDULED: colors.statusRed,
    SCHEDULED: colors.statusBlue,
  };

  const colorScale = scaleOrdinal<TestStatus, string>({
    domain: Object.keys(colorMap) as TestStatus[],
    range: Object.values(colorMap),
  });

  dateScale.rangeRound([0, xMax]);

  const columns = [
    {
      header: 'Month',
      id: 'month',
      accessor: (row: Datum) => {
        return formatDateLong(row.month);
      },
    },
    ...keys.map((key) => {
      return {
        header: key,
        accessor: key,
      };
    }),
  ].map(assignColumnAccessor);

  const downloadCsv = () => {
    const filename = 'test-status.csv';
    const csv = papaparse.unparse({
      fields: columns.map((column) => column.header),
      data: data.map((item) => columns.map((column) => column.accessor(item))),
    });
    downloadFile(filename, csv);
  };

  return (
    // relative position is needed for correct tooltip positioning
    <Box
      sx={{
        position: 'relative',
        '.test-status-bar:hover': {
          opacity: 0.8,
        },
      }}
    >
      <Flex
        sx={{
          position: 'absolute',
          width: '100%',
          top: 1,
          justifyContent: 'center',
          fontSize: '14px',
        }}
      >
        <Legend<TestStatus>
          colorScale={colorScale}
          disabledKeys={disabledKeys}
          toggleKey={toggleKey}
        />
      </Flex>

      <svg width={width} height={height}>
        <Group top={margin.top}>
          <BarStack<Datum, TestStatus>
            data={data}
            keys={statuses.filter((status) => !disabledKeys[status])}
            x={getMonth}
            xScale={dateScale}
            yScale={yScale}
            color={colorScale}
          >
            {(barStacks) =>
              barStacks.map((barStack) =>
                barStack.bars.map((bar) => {
                  return (
                    <rect
                      className="test-status-bar"
                      key={`bar-stack-${barStack.index}-${bar.index}`}
                      x={bar.x}
                      y={bar.y}
                      cursor="pointer"
                      height={bar.height}
                      width={bar.width}
                      fill={bar.color}
                      onClick={() => {
                        if (!events) {
                          return;
                        }
                        // alert(`clicked: ${JSON.stringify(bar)}`)
                        const month = months[bar.index];
                        const monthPadded = (month + '').padStart(2, '0');
                        const startDate = new Date(
                          new Date().getFullYear(),
                          bar.index,
                          1,
                          0,
                          0,
                          0
                        );

                        const lastDay: number = endOfMonth(startDate).getDate();
                        const lastDayPadded = (lastDay + '').padStart(2, '0');
                        const year = new Date().getFullYear();

                        storeFilters({
                          ...defaultFilters,
                          testStatus: [bar.key],
                          dateField: 'testAnniversary',
                          dateStart: `${year}-${monthPadded}-01`,
                          dateEnd: `${year}-${monthPadded}-${lastDayPadded}`,
                        });
                        history.push(`/${mode}/assemblies`);
                      }}
                      onMouseLeave={() => {
                        tooltipTimeout = window.setTimeout(() => {
                          hideTooltip();
                        }, 300);
                      }}
                      onMouseMove={(event) => {
                        if (tooltipTimeout) {
                          clearTimeout(tooltipTimeout);
                        }
                        const top = Math.max(bar.y - 80, 0);
                        const left = Math.min(
                          bar.x - margin.left - 10,
                          width - (tooltipStyles.minWidth as number) - 40
                        );
                        showTooltip({
                          tooltipData: bar,
                          tooltipTop: top,
                          tooltipLeft: left,
                        });
                      }}
                    />
                  );
                })
              )
            }
          </BarStack>
        </Group>
        <AxisBottom
          top={yMax + margin.top}
          scale={dateScale}
          tickFormat={formatDate}
          stroke={colors.text}
          tickStroke={colors.text}
          hideTicks
          hideAxisLine
          tickLabelProps={() => ({
            fill: colors.text,
            fontSize: 12,
            textAnchor: 'middle',
          })}
        />
      </svg>

      <Tooltip
        key={Math.random()} // update tooltip bounds each render
        top={tooltipTop}
        left={tooltipLeft}
        style={tooltipStyles}
        hidden={!tooltipOpen}
      >
        {tooltipData && (
          <>
            <Text
              variant="heading2"
              style={{ color: colorScale(tooltipData.key) }}
            >
              {formatDateLong(getMonth(tooltipData.bar.data))}
            </Text>
            <Flex mt={2} justifyContent="space-between">
              <Text
                variant="heading3"
                color="#bbb"
                fontWeight="bold"
                sx={{ textTransform: 'uppercase' }}
              >
                {constantToLabel(tooltipData.key)}{' '}
              </Text>
              <Text variant="heading3">
                {tooltipData.bar.data[tooltipData.key]}
              </Text>
            </Flex>
          </>
        )}
      </Tooltip>
      <Button
        sx={{ position: 'absolute', top: 0, right: 0 }}
        icon="download"
        onClick={downloadCsv}
      />
    </Box>
  );
};

// export default TestStatusWidget
export default Visx;
